import { useState, useEffect, useMemo, useRef, useCallback } from 'react';import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent } from '../ui/card';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import { Plus, Pencil, Trash2, ChefHat, AlertCircle, X, Search, Check, Layers, Loader2, Maximize2, Minimize2, Download, Store, FileSpreadsheet, FileText, Eye, Calendar, Image as ImageIcon, Instagram } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import AssignOutletsDialog from './AssignOutletsDialog';
import ViewOutletsDialog from './ViewOutletsDialog';
import { downloadRecipeExport } from './recipeExport';
import { TaxonomySelect } from './TaxonomySelect';
import { Checkbox } from '../ui/checkbox';
import { fmtDateIST } from '../../lib/tz';


const fmtINR = (v) => `₹${(Number(v) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;

/* ---------- Filter chip row — horizontal scroll, multi-select ---------- */
function FilterChipRow({ label, options, selected, onToggle, onClear, testIdPrefix }) {
  return (
    <div
      className="flex items-center gap-2 px-2 py-1.5 rounded-md bg-[#FAF7F0]/60 border border-[#E8DECF]"
      data-testid={`${testIdPrefix}-row`}
    >
      <span className="text-[10px] font-bold uppercase tracking-wider text-[#7A5A4B] shrink-0 pl-1">
        {label}
      </span>
      <div className="flex items-center gap-1.5 overflow-x-auto flex-1 min-w-0 scrollbar-thin">
        {options.map(opt => {
          const isActive = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => onToggle(opt)}
              data-testid={`${testIdPrefix}-chip-${opt.toLowerCase().replace(/\s+/g, '-')}`}
              data-active={isActive}
              className={`shrink-0 h-7 px-3 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all ${
                isActive
                  ? 'bg-[#E8643E] text-white shadow-sm'
                  : 'bg-white text-[#3A1F12] border border-[#E8DECF] hover:border-[#A0846B]'
              }`}
            >
              {opt}
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <button
          type="button"
          onClick={onClear}
          data-testid={`${testIdPrefix}-clear`}
          className="text-[10px] font-semibold text-[#7A2A12] hover:underline shrink-0 pr-1"
        >
          Clear ({selected.length})
        </button>
      )}
    </div>
  );
}


// Shared filter helper — applies sub/top/all mode + category filter
function applyFilters(recipes, mode, categoryFilter, cuisineFilters = [], catFilters = []) {
  let out = recipes;
  if (mode === 'sub') out = out.filter(r => r.is_sub_recipe);
  else if (mode === 'top') out = out.filter(r => !r.is_sub_recipe);
  if (categoryFilter && categoryFilter !== 'all') {
    out = out.filter(r => (r.category_type || 'food') === categoryFilter);
  }
  if (cuisineFilters.length > 0) {
    const set = new Set(cuisineFilters.map(s => s.toLowerCase()));
    out = out.filter(r => set.has(((r.cuisine || '').trim()).toLowerCase()));
  }
  if (catFilters.length > 0) {
    const set = new Set(catFilters.map(s => s.toLowerCase()));
    out = out.filter(r => set.has(((r.category || '').trim()).toLowerCase()));
  }
  return out;
}

/* ---------- Qty input — adapts labels to the ingredient's pack_unit ----------
   Internal storage always uses qty_kg (major) + qty_grams (minor). Label/placeholder
   switches between kg/g, L/ml, or a single 'pc' count based on `packUnit`.            */
function QtyInput({ packUnit = 'kg', kg, grams, onChange, disabled, idPrefix }) {
  const u = (packUnit || 'kg').toLowerCase();
  const isVolume = u === 'l' || u === 'ml';
  const isCount = u === 'pc';

  if (isCount) {
    return (
      <div className="relative">
        <Input type="number" inputMode="numeric" step="1" min="0"
          value={kg ?? ''} disabled={disabled}
          onChange={e => onChange({ kg: e.target.value, grams: 0 })}
          placeholder="pieces"
          data-testid={`${idPrefix}-pc`}
          className="h-9 pr-10 bg-white text-xs tabular-nums" />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-[#5C6B64]">pc</span>
      </div>
    );
  }

  const majorLabel = isVolume ? 'L' : 'kg';
  const minorLabel = isVolume ? 'ml' : 'g';
  const majorTid = isVolume ? 'L' : 'kg';
  const minorTid = isVolume ? 'ml' : 'grams';
  return (
    <div className="grid grid-cols-2 gap-1.5">
      <div className="relative">
        <Input type="number" inputMode="decimal" step="any" min="0"
          value={kg ?? ''} disabled={disabled}
          onChange={e => onChange({ kg: e.target.value, grams })}
          placeholder={majorLabel}
          data-testid={`${idPrefix}-${majorTid}`}
          className="h-9 pr-7 bg-white text-xs tabular-nums" />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-[#5C6B64]">{majorLabel}</span>
      </div>
      <div className="relative">
        <Input type="number" inputMode="decimal" step="any" min="0"
          value={grams ?? ''} disabled={disabled}
          onChange={e => onChange({ kg, grams: e.target.value })}
          placeholder={minorLabel}
          data-testid={`${idPrefix}-${minorTid}`}
          className="h-9 pr-8 bg-white text-xs tabular-nums" />
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-semibold text-[#5C6B64]">{minorLabel}</span>
      </div>
    </div>
  );
}

/* Legacy name kept for output-weight field (always kg+grams) */
function KgGramsInput(props) {
  return <QtyInput packUnit="kg" {...props} />;
}

/* ---------- Typeahead combobox for raw + sub-recipe ---------- */
function IngredientCombobox({ value, onSelect, items, excludeId, testId }) {
  const [open, setOpen] = useState(false);
  const selected = items.find(i => i.reference_id === value);
  const filtered = excludeId
    ? items.filter(i => i.reference_id !== excludeId)
    : items;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="w-full h-9 px-3 flex items-center justify-between gap-2 rounded-md border border-input bg-white text-xs hover:border-[color:var(--color-primary)]/60 transition-colors">
          {selected ? (
            <span className="flex items-center gap-1.5 truncate text-left">
              {selected.kind === 'sub_recipe' && (
                <span className="w-1.5 h-1.5 rounded-full bg-[#1D4ED8] shrink-0" aria-hidden />
              )}
              <span className="truncate text-[#1A2421] font-medium">{selected.label}</span>
              <span className="text-[#5C6B64] text-[10px] shrink-0">{selected.secondary}</span>
            </span>
          ) : (
            <span className="text-[#5C6B64]">Select ingredient…</span>
          )}
          <Search className="w-3.5 h-3.5 text-[#5C6B64] shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="start">
        <Command filter={(val, s) => (val.toLowerCase().includes(s.toLowerCase()) ? 1 : 0)}>
          <CommandInput placeholder="Type to search ingredients or sub-recipes…" className="text-xs" />
          <CommandList className="max-h-[260px]">
            <CommandEmpty className="py-6 text-center text-xs text-[#5C6B64]">No matches</CommandEmpty>
            {['sub_recipe', 'raw'].map(kind => {
              const rows = filtered.filter(i => i.kind === kind);
              if (!rows.length) return null;
              return (
                <CommandGroup key={kind} heading={kind === 'sub_recipe' ? 'Sub-recipes' : 'Raw ingredients'}>
                  {rows.map(i => (
                    <CommandItem key={i.reference_id} value={`${i.label} ${i.secondary}`}
                      onSelect={() => { onSelect(i); setOpen(false); }}
                      className="text-xs cursor-pointer">
                      <div className="flex items-center gap-2 w-full">
                        {kind === 'sub_recipe' ? (
                          <Layers className="w-3.5 h-3.5 text-[#1D4ED8]" />
                        ) : (
                          <span className="w-3.5 h-3.5" />
                        )}
                        <span className="flex-1 font-medium text-[#1A2421]">{i.label}</span>
                        <span className="text-[10px] text-[#5C6B64]">{i.secondary}</span>
                        {i.reference_id === value && <Check className="w-3 h-3 text-[color:var(--color-primary)]" />}
                      </div>
                    </CommandItem>
                  ))}
                </CommandGroup>
              );
            })}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

/* ---------- Main recipe builder dialog ---------- */
function RecipeBuilder({ recipe, onClose, onSaved, taxonomy, refreshTaxonomy }) {
  const { getHeaders, API } = useAuth();

  const [id, setId] = useState(recipe?.id || null);
  const [name, setName] = useState(recipe?.name || '');
  const [categoryType, setCategoryType] = useState(recipe?.category_type || 'food');
  const [cuisine, setCuisine] = useState(recipe?.cuisine || '');
  const [category, setCategory] = useState(recipe?.category || '');
  const [outputType, setOutputType] = useState(recipe?.output_type || 'portion');
  const [outPortions, setOutPortions] = useState(recipe?.output_portions ?? '');
  const [outKg, setOutKg] = useState(recipe?.output_kg ?? '');
  const [outGrams, setOutGrams] = useState(recipe?.output_grams ?? '');
  const [portionsCount, setPortionsCount] = useState(recipe?.portions_count ?? '');
  const [isSubRecipe, setIsSubRecipe] = useState(!!recipe?.is_sub_recipe);
  const [preparationMethod, setPreparationMethod] = useState(recipe?.preparation_method || '');
  const [chefNotes, setChefNotes] = useState(recipe?.chef_notes || '');
  const [imageB64, setImageB64] = useState(recipe?.image_b64 || '');
  const [lines, setLines] = useState(
    (recipe?.ingredients || []).map(l => ({
      type: l.type || 'raw',
      reference_id: l.reference_id || l.ingredient_id || '',
      qty_kg: l.qty_kg ?? '',
      qty_grams: l.qty_grams ?? '',
      yield_pct: l.yield_pct ?? 100,
    })),
  );
  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [live, setLive] = useState({ total_cost: recipe?.total_cost ?? 0, cost_per_portion: recipe?.cost_per_portion ?? 0, cost_per_kg: recipe?.cost_per_kg ?? 0, portion_weight_grams: recipe?.portion_weight_grams ?? 0, ingredients: recipe?.ingredients || [] });
  const [items, setItems] = useState([]);
  const [fullScreen, setFullScreen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const saveTimer = useRef(null);
  const firstRender = useRef(true);

  // Load the typeahead catalogue once
  const loadItems = useCallback(async () => {
    const { data } = await axios.get(`${API}/recipes/search?limit=2000`, { headers: getHeaders() });
    setItems(data.items || []);
  }, [API, getHeaders]);

  useEffect(() => { loadItems(); }, [loadItems]);

  const itemMap = useMemo(() => {
    const m = {}; items.forEach(i => { m[i.reference_id] = i; }); return m;
  }, [items]);

  const subRecipeDisabledReason = outputType !== 'weight'
    ? 'Only weight-output recipes can be reused as ingredients'
    : null;

  // Build payload
  const payload = useMemo(() => {
    const cleanLines = lines.filter(l => l.reference_id).map(l => ({
      type: l.type || 'raw',
      reference_id: l.reference_id,
      qty_kg: Number(l.qty_kg) || 0,
      qty_grams: Number(l.qty_grams) || 0,
      yield_pct: Math.max(1, Math.min(100, Number(l.yield_pct) || 100)),
    }));
    const body = {
      name: name.trim(),
      category_type: categoryType,
      cuisine: (cuisine || '').trim(),
      category: (category || '').trim(),
      output_type: outputType,
      is_sub_recipe: isSubRecipe && outputType === 'weight',
      preparation_method: (preparationMethod || '').trim(),
      chef_notes: (chefNotes || '').trim(),
      image_b64: imageB64 || '',
      ingredients: cleanLines,
    };
    if (outputType === 'portion') body.output_portions = Number(outPortions) || 0;
    else {
      body.output_kg = Number(outKg) || 0;
      body.output_grams = Number(outGrams) || 0;
      body.portions_count = Number(portionsCount) || 0;
    }
    return body;
  }, [name, categoryType, cuisine, category, outputType, outPortions, outKg, outGrams, portionsCount, isSubRecipe, preparationMethod, chefNotes, imageB64, lines]);

  const isValid = useMemo(() => {
    if (!payload.name) return false;
    if (payload.output_type === 'portion' && !(payload.output_portions > 0)) return false;
    if (payload.output_type === 'weight') {
      const total = (payload.output_kg || 0) + (payload.output_grams || 0) / 1000;
      if (!(total > 0)) return false;
    }
    if (payload.ingredients.length === 0) return false;
    for (const l of payload.ingredients) {
      const q = (l.qty_kg || 0) + (l.qty_grams || 0) / 1000;
      if (!(q > 0)) return false;
    }
    return true;
  }, [payload]);

  // Autosave with debounce
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    if (!isValid) return;
    clearTimeout(saveTimer.current);
    setSaveState('saving');
    saveTimer.current = setTimeout(async () => {
      try {
        const res = id
          ? await axios.put(`${API}/recipes/${id}`, payload, { headers: getHeaders() })
          : await axios.post(`${API}/recipes`, payload, { headers: getHeaders() });
        if (!id) setId(res.data.id);
        setLive({
          total_cost: res.data.total_cost,
          cost_per_portion: res.data.cost_per_portion,
          cost_per_kg: res.data.cost_per_kg,
          portion_weight_grams: res.data.portion_weight_grams || 0,
          ingredients: res.data.ingredients || [],
        });
        setSaveState('saved');
        onSaved?.();   // refresh parent list silently
      } catch (err) {
        setSaveState('error');
        const msg = err?.response?.data?.detail || 'Save failed';
        toast.error(msg);
      }
    }, 700);
    return () => clearTimeout(saveTimer.current);
  }, [payload, isValid, id, API, getHeaders, onSaved]);

  // Client-side live preview (mirrors server math using cached `items` unit_price)
  const preview = useMemo(() => {
    let total = 0;
    const rows = lines.map(l => {
      const meta = itemMap[l.reference_id];
      const unit = (meta?.pack_unit || (meta?.kind === 'sub_recipe' ? 'kg' : 'kg')).toLowerCase();
      const major = Number(l.qty_kg) || 0;
      const minor = Number(l.qty_grams) || 0;
      let qtyInUnit = 0;
      if (unit === 'g') qtyInUnit = major * 1000 + minor;
      else if (unit === 'l') qtyInUnit = major + minor / 1000;
      else if (unit === 'ml') qtyInUnit = major * 1000 + minor;
      else if (unit === 'pc') qtyInUnit = major;
      else qtyInUnit = major + minor / 1000;   // kg (default)
      const yp = Number(l.yield_pct) || 100;
      const unit_price = Number(meta?.price) || 0;     // ₹ / pack_unit
      let eff = qtyInUnit * unit_price;
      if (yp > 0 && yp < 100) eff = eff / (yp / 100);
      total += eff;
      return { ...l, meta, eff, unit_price, pack_unit: unit };
    });
    let per_portion = 0, per_kg = 0, portion_weight_g = 0;
    if (outputType === 'portion' && Number(outPortions) > 0) per_portion = total / Number(outPortions);
    if (outputType === 'weight') {
      const okg = (Number(outKg) || 0) + (Number(outGrams) || 0) / 1000;
      if (okg > 0) per_kg = total / okg;
      const pc = Number(portionsCount) || 0;
      if (pc > 0) {
        per_portion = total / pc;
        if (okg > 0) portion_weight_g = (okg * 1000) / pc;
      }
    }
    return { rows, total, per_portion, per_kg, portion_weight_g };
  }, [lines, itemMap, outputType, outPortions, outKg, outGrams, portionsCount]);

  const addLine = () => setLines(ls => [...ls, { type: 'raw', reference_id: '', qty_kg: '', qty_grams: '', yield_pct: 100 }]);
  const removeLine = (idx) => setLines(ls => ls.filter((_, i) => i !== idx));
  const updateLine = (idx, patch) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));
  const selectIngredient = (idx, item) => updateLine(idx, { type: item.kind, reference_id: item.reference_id });

  const handleDoneClose = async () => {
    if (saveState === 'saving') return;
    // If user clicks Done while a debounced autosave is still pending,
    // flush it synchronously so the recipe is persisted before the dialog closes.
    if (saveTimer.current && isValid) {
      clearTimeout(saveTimer.current);
      setSaveState('saving');
      try {
        const res = id
          ? await axios.put(`${API}/recipes/${id}`, payload, { headers: getHeaders() })
          : await axios.post(`${API}/recipes`, payload, { headers: getHeaders() });
        if (!id) setId(res.data.id);
        setSaveState('saved');
        onSaved?.();
      } catch (err) {
        setSaveState('error');
        toast.error(err?.response?.data?.detail || 'Save failed');
        return;  // don't close if save failed — let user retry / cancel
      }
    }
    onClose();
  };

  const statusPill = () => {
    if (saveState === 'saving') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full"><Loader2 className="w-3 h-3 animate-spin" /> Saving…</span>;
    if (saveState === 'saved') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid="autosave-saved"><Check className="w-3 h-3" /> Saved</span>;
    if (saveState === 'error') return <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#C45A41] bg-[#FEF2EE] border border-[#C45A41]/30 px-2 py-0.5 rounded-full"><AlertCircle className="w-3 h-3" /> Save failed</span>;
    return null;
  };

  return (
    <Dialog open onOpenChange={o => !o && handleDoneClose()}>
      <DialogContent
        className={
          fullScreen
            ? '!max-w-[96vw] !w-[96vw] max-h-[96vh] h-[96vh] overflow-y-auto p-0'
            : 'max-w-4xl max-h-[92vh] overflow-y-auto'
        }
        data-testid="recipe-builder-dialog">
        <DialogHeader
          className={fullScreen ? 'sticky top-0 z-10 bg-white/95 backdrop-blur border-b border-[#E2E8DF] px-6 pt-5 pb-4' : ''}>
          <DialogTitle className="flex items-center gap-2">
            <ChefHat className="w-4 h-4 text-[color:var(--color-primary)]" />
            {id ? 'Edit recipe' : 'New recipe'}
            <div className="ml-auto flex items-center gap-2">
              {statusPill()}
              {id && (
                <>
                  <Button type="button" variant="outline" size="sm"
                    onClick={() => setAssignOpen(true)} className="h-8"
                    data-testid="recipe-assign-outlets-btn">
                    <Store className="w-3.5 h-3.5 mr-1.5" /> Assign to Outlets
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button type="button" variant="outline" size="sm" className="h-8"
                        data-testid="recipe-export-btn">
                        <Download className="w-3.5 h-3.5 mr-1.5" /> Export
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => downloadRecipeExport({ API, getHeaders, id, format: 'xlsx' })}
                        data-testid="recipe-export-xlsx">
                        <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Excel (.xlsx)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => downloadRecipeExport({ API, getHeaders, id, format: 'pdf' })}
                        data-testid="recipe-export-pdf">
                        <FileText className="w-3.5 h-3.5 mr-1.5" /> PDF
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => downloadRecipeExport({ API, getHeaders, id, format: 'jpg', variant: 'social' })}
                        data-testid="recipe-export-jpg-social">
                        <Instagram className="w-3.5 h-3.5 mr-1.5" /> Instagram card (1080×1350)
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => downloadRecipeExport({ API, getHeaders, id, format: 'jpg', variant: 'full' })}
                        data-testid="recipe-export-jpg-full">
                        <ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Full recipe JPG
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              )}
              <button type="button" onClick={() => setFullScreen(v => !v)}
                data-testid="recipe-fullscreen-toggle"
                title={fullScreen ? 'Collapse' : 'Expand to full screen'}
                className="w-8 h-8 rounded-md text-[#5C6B64] hover:bg-[#F3F5F2] flex items-center justify-center">
                {fullScreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
            </div>
          </DialogTitle>
          <DialogDescription className="sr-only">
            Build a recipe with ingredients, quantities in kg and grams, yield percentages, and output type. Changes autosave.
          </DialogDescription>
        </DialogHeader>

        <div className={`space-y-4 py-2 ${fullScreen ? 'px-6' : ''}`}>
          {/* Name + category + sub-recipe toggle */}
          <div className="grid grid-cols-1 sm:grid-cols-[1fr_130px_auto] gap-3 items-end">
            <div>
              <Label className="text-xs font-semibold text-[#1A2421]">Recipe name</Label>
              <Input value={name} onChange={e => setName(e.target.value)}
                placeholder="e.g., Paneer Tikka Masala" maxLength={150}
                data-testid="recipe-name-input" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#1A2421]">Category</Label>
              <div className="mt-1 grid grid-cols-2 gap-1 p-0.5 rounded-md bg-[#F3F5F2] border border-[#E2E8DF]">
                {[{ k: 'food', label: 'Food' }, { k: 'beverage', label: 'Bev' }].map(o => (
                  <button key={o.k} type="button"
                    onClick={() => setCategoryType(o.k)}
                    data-testid={`recipe-category-${o.k}`}
                    className={`h-8 rounded text-xs font-semibold transition-colors ${categoryType === o.k ? 'bg-white shadow-sm text-[color:var(--color-primary)]' : 'text-[#5C6B64] hover:text-[#1A2421]'}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 p-2 rounded-lg border border-[#E2E8DF] bg-[#F9F9F8]">
              <Switch checked={isSubRecipe} disabled={!!subRecipeDisabledReason}
                onCheckedChange={setIsSubRecipe}
                data-testid="recipe-is-sub-toggle" />
              <div className="flex-1">
                <p className="text-[11px] font-semibold text-[#1A2421] leading-tight">Use as Ingredient</p>
                <p className="text-[9px] text-[#5C6B64] leading-tight">
                  {subRecipeDisabledReason || 'Makes this available in other recipes'}
                </p>
              </div>
            </div>
          </div>

          {/* Cuisine + Category — structured creator taxonomy */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-[#1A2421]">Cuisine</Label>
              <p className="text-[10px] text-[#5C6B64] mb-1">Geographic / cultural style — helps with search &amp; library grouping.</p>
              <TaxonomySelect
                value={cuisine}
                options={taxonomy?.cuisines || []}
                placeholder="Select cuisine…"
                onChange={setCuisine}
                onCreate={async (val) => {
                  try {
                    await axios.post(`${API}/recipes/taxonomy`, { kind: 'cuisine', value: val }, { headers: getHeaders() });
                    await refreshTaxonomy?.();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Could not save cuisine');
                  }
                }}
                testIdPrefix="recipe-cuisine"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#1A2421]">Category</Label>
              <p className="text-[10px] text-[#5C6B64] mb-1">Menu structure (Starters / Pizza / Curry…) — used in exports &amp; filters.</p>
              <TaxonomySelect
                value={category}
                options={taxonomy?.categories || []}
                placeholder="Select category…"
                onChange={setCategory}
                onCreate={async (val) => {
                  try {
                    await axios.post(`${API}/recipes/taxonomy`, { kind: 'category', value: val }, { headers: getHeaders() });
                    await refreshTaxonomy?.();
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || 'Could not save category');
                  }
                }}
                testIdPrefix="recipe-category-tag"
              />
            </div>
          </div>

          {/* Output type */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs font-semibold text-[#1A2421]">Output type</Label>
              <div className="mt-1 grid grid-cols-2 gap-1 p-0.5 rounded-md bg-[#F3F5F2] border border-[#E2E8DF]">
                {[{ k: 'portion', label: 'Portions' }, { k: 'weight', label: 'Weight' }].map(o => (
                  <button key={o.k} type="button"
                    onClick={() => setOutputType(o.k)}
                    data-testid={`recipe-outtype-${o.k}`}
                    className={`h-8 rounded text-xs font-semibold transition-colors ${outputType === o.k ? 'bg-white shadow-sm text-[color:var(--color-primary)]' : 'text-[#5C6B64] hover:text-[#1A2421]'}`}>
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="sm:col-span-2">
              {outputType === 'portion' ? (
                <>
                  <Label className="text-xs font-semibold text-[#1A2421]">Output portions</Label>
                  <Input type="number" inputMode="decimal" step="any" min="0.1"
                    value={outPortions} onChange={e => setOutPortions(e.target.value)}
                    placeholder="e.g., 4"
                    data-testid="recipe-portions-input" className="mt-1 h-9" />
                </>
              ) : (
                <>
                  <Label className="text-xs font-semibold text-[#1A2421]">Output weight</Label>
                  <div className="mt-1">
                    <KgGramsInput kg={outKg} grams={outGrams}
                      onChange={({ kg, grams }) => { setOutKg(kg); setOutGrams(grams); }}
                      idPrefix="recipe-output" />
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Selling-conversion layer: weight-output only.
              Optional. Lets the chef define how many portions are plated from the bulk output.
              When blank → behaves exactly like before (no per-portion math shown). */}
          {outputType === 'weight' && (
            <div className="grid grid-cols-1 sm:grid-cols-[200px_1fr] gap-3 items-start p-3 rounded-lg border border-[color:var(--color-primary)]/15 bg-[color:var(--color-primary)]/[0.03]"
              data-testid="recipe-portions-conversion-block">
              <div>
                <Label className="text-xs font-semibold text-[#1A2421]">
                  Portions from this recipe
                </Label>
                <Input type="number" inputMode="numeric" step="1" min="0"
                  value={portionsCount} onChange={e => setPortionsCount(e.target.value)}
                  placeholder="e.g., 25 (optional)"
                  data-testid="recipe-portions-count-input" className="mt-1 h-9" />
                <p className="text-[10px] text-[#5C6B64] mt-1 leading-snug">
                  How many sellable portions this batch yields. Leave blank to keep ₹/kg-only.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-2 self-stretch">
                <div className="rounded-md border border-[#E2E8DF] bg-white px-3 py-2">
                  <p className="text-[9px] uppercase tracking-wider text-[#5C6B64]">Portion size</p>
                  <p className="text-sm font-bold text-[color:var(--color-primary)] tabular-nums mt-0.5"
                    data-testid="recipe-portion-size">
                    {Number(portionsCount) > 0 && preview.portion_weight_g > 0
                      ? `${preview.portion_weight_g >= 1000
                          ? `${(preview.portion_weight_g / 1000).toFixed(2)} kg`
                          : `${Math.round(preview.portion_weight_g)} g`}`
                      : '—'}
                  </p>
                </div>
                <div className="rounded-md border border-[#E2E8DF] bg-white px-3 py-2">
                  <p className="text-[9px] uppercase tracking-wider text-[#5C6B64]">Cost / portion</p>
                  <p className="text-sm font-bold text-[color:var(--color-primary)] tabular-nums mt-0.5"
                    data-testid="recipe-portion-cost">
                    {Number(portionsCount) > 0 && preview.per_portion > 0
                      ? fmtINR(saveState === 'saved' ? live.cost_per_portion : preview.per_portion)
                      : '—'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Ingredient lines */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-xs font-semibold text-[#1A2421]">Ingredients</Label>
              <span className="text-[10px] text-[#5C6B64]">
                {lines.length} line{lines.length === 1 ? '' : 's'}
              </span>
            </div>
            {lines.length === 0 ? (
              <div className="py-6 text-center border border-dashed border-[#E2E8DF] rounded-lg">
                <p className="text-xs text-[#5C6B64]">No ingredients yet — click the button below to add one.</p>
              </div>
            ) : (
              <div className="space-y-2">
                {preview.rows.map((row, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end p-2.5 rounded-lg border border-[#E2E8DF] bg-[#F9F9F8]"
                    data-testid={`recipe-line-${idx}`}>
                    <div className="col-span-12 sm:col-span-5">
                      <Label className="text-[10px] text-[#5C6B64] uppercase tracking-wider">Ingredient</Label>
                      <div className="mt-1">
                        <IngredientCombobox
                          value={row.reference_id}
                          items={items}
                          excludeId={id /* prevent self-selection when editing */}
                          onSelect={(i) => selectIngredient(idx, i)}
                          testId={`recipe-line-ing-${idx}`} />
                      </div>
                    </div>
                    <div className="col-span-7 sm:col-span-3">
                      <Label className="text-[10px] text-[#5C6B64] uppercase tracking-wider">Quantity</Label>
                      <div className="mt-1">
                        <QtyInput
                          packUnit={row.pack_unit}
                          kg={row.qty_kg}
                          grams={row.qty_grams}
                          onChange={({ kg, grams }) => updateLine(idx, { qty_kg: kg, qty_grams: grams })}
                          idPrefix={`recipe-line-qty-${idx}`} />
                      </div>
                    </div>
                    <div className="col-span-3 sm:col-span-2">
                      <Label className="text-[10px] text-[#5C6B64] uppercase tracking-wider">Yield %</Label>
                      <Input type="number" inputMode="decimal" step="any" min="1" max="100"
                        value={row.yield_pct} onChange={e => updateLine(idx, { yield_pct: e.target.value })}
                        placeholder="100"
                        data-testid={`recipe-line-yield-${idx}`} className="h-9 mt-1 bg-white text-xs" />
                    </div>
                    <div className="col-span-2 sm:col-span-2 flex items-end gap-1">
                      <div className="flex-1">
                        <Label className="text-[10px] text-[#5C6B64] uppercase tracking-wider">Cost</Label>
                        <div className="h-9 mt-1 px-2 flex items-center rounded-md border border-[#E2E8DF] bg-white text-xs font-semibold text-[color:var(--color-primary)] tabular-nums"
                          data-testid={`recipe-line-cost-${idx}`}>
                          {fmtINR(row.eff)}
                        </div>
                      </div>
                      <button type="button" onClick={() => removeLine(idx)}
                        data-testid={`recipe-line-remove-${idx}`}
                        className="w-8 h-9 rounded-md text-[#C45A41] hover:bg-[#C45A41]/10 flex items-center justify-center">
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Bottom-placed Add ingredient button — always at the end of the list */}
            <div className="mt-3 flex justify-end">
              <Button variant="outline" size="sm" onClick={addLine}
                data-testid="recipe-add-line-btn"
                className="h-9 border-dashed border-[color:var(--color-primary)]/40 text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary)]/5">
                <Plus className="w-3.5 h-3.5 mr-1.5" /> Add ingredient
              </Button>
            </div>
          </div>

          {/* Live totals — show server values when available, fall back to client preview */}
          <Card className="border-[color:var(--color-primary)]/20 bg-[color:var(--color-primary)]/[0.04]">
            <CardContent className="px-4 py-3 grid grid-cols-2 gap-4">
              <div>
                <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">Total recipe cost</p>
                <p className="text-xl font-bold text-[color:var(--color-primary)] tabular-nums" data-testid="recipe-total-cost">
                  {fmtINR(saveState === 'saved' ? live.total_cost : preview.total)}
                </p>
              </div>
              <div className="text-right">
                <p className="text-[10px] uppercase tracking-wider text-[#5C6B64]">
                  {outputType === 'weight' ? 'Cost / kg' : 'Cost / portion'}
                </p>
                <p className="text-xl font-bold text-[color:var(--color-primary)] tabular-nums" data-testid="recipe-cost-per-portion">
                  {outputType === 'weight'
                    ? fmtINR(saveState === 'saved' ? live.cost_per_kg : preview.per_kg)
                    : fmtINR(saveState === 'saved' ? live.cost_per_portion : preview.per_portion)}
                </p>
              </div>
            </CardContent>
          </Card>

          {items.length === 0 && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <AlertCircle className="w-4 h-4 text-amber-700 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-800">Add ingredients in the <strong>Ingredients</strong> tab first.</p>
            </div>
          )}

          {/* Creator richness — Preparation Method / Chef Notes / Recipe Image */}
          <div className="border-t border-[#E2E8DF] pt-4 mt-2 space-y-3" data-testid="recipe-richness-block">
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_180px] gap-3 items-start">
              <div>
                <Label className="text-xs font-semibold text-[#1A2421]">Preparation method</Label>
                <p className="text-[10px] text-[#5C6B64] mb-1">Numbered steps work great. Saved automatically.</p>
                <textarea value={preparationMethod}
                  onChange={e => setPreparationMethod(e.target.value)}
                  rows={5}
                  data-testid="recipe-prep-method-input"
                  placeholder={'1. Heat oil…\n2. Add onions…\n3. Simmer 20 min…'}
                  className="w-full text-sm rounded-md border border-[#E2E8DF] px-3 py-2 bg-white focus:outline-none focus:border-[color:var(--color-primary)] resize-y" />
              </div>
              <div>
                <Label className="text-xs font-semibold text-[#1A2421]">Recipe image (optional)</Label>
                <p className="text-[10px] text-[#5C6B64] mb-1">Used in PDF & exports.</p>
                {imageB64 ? (
                  <div className="relative group">
                    <img src={imageB64} alt="Recipe"
                      data-testid="recipe-image-preview"
                      className="w-full h-32 object-cover rounded-md border border-[#E2E8DF]" />
                    <button type="button"
                      onClick={() => setImageB64('')}
                      data-testid="recipe-image-clear"
                      className="absolute top-1 right-1 bg-white/95 hover:bg-white text-red-600 text-[10px] font-semibold px-2 py-0.5 rounded shadow">
                      Remove
                    </button>
                  </div>
                ) : (
                  <label className="flex flex-col items-center justify-center h-32 rounded-md border-2 border-dashed border-[#E2E8DF] hover:border-[color:var(--color-primary)]/40 hover:bg-[color:var(--color-primary)]/[0.03] cursor-pointer transition-colors"
                    data-testid="recipe-image-upload">
                    <span className="text-[11px] text-[#5C6B64]">Click to upload</span>
                    <span className="text-[9px] text-[#5C6B64] mt-0.5">JPG / PNG · max 1.5 MB</span>
                    <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
                      onChange={e => {
                        const f = e.target.files?.[0];
                        if (!f) return;
                        if (f.size > 1.5 * 1024 * 1024) { toast.error('Image too large (max 1.5 MB)'); return; }
                        const reader = new FileReader();
                        reader.onload = () => setImageB64(reader.result);
                        reader.readAsDataURL(f);
                      }} />
                  </label>
                )}
              </div>
            </div>
            <div>
              <Label className="text-xs font-semibold text-[#1A2421]">Chef's notes (optional)</Label>
              <p className="text-[10px] text-[#5C6B64] mb-1">Tips, tricks, plating ideas — appears in PDF exports.</p>
              <textarea value={chefNotes}
                onChange={e => setChefNotes(e.target.value)}
                rows={2}
                data-testid="recipe-chef-notes-input"
                placeholder="Use Pakistani basmati for the longest grain. Bloom saffron in milk, never water."
                className="w-full text-sm rounded-md border border-[#E2E8DF] px-3 py-2 bg-white focus:outline-none focus:border-[color:var(--color-primary)] resize-y" />
            </div>
          </div>
        </div>

        <DialogFooter className={fullScreen ? 'sticky bottom-0 bg-white border-t border-[#E2E8DF] px-6 py-3' : ''}>
          <Button onClick={handleDoneClose}
            className="bg-[color:var(--color-primary)] hover:bg-[#1A4D3C] text-white"
            data-testid="recipe-done-btn">
            Done
          </Button>
        </DialogFooter>
        {assignOpen && id && (
          <AssignOutletsDialog
            open={assignOpen}
            onClose={() => setAssignOpen(false)}
            recipeId={id}
            recipeName={name}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Recipes list ---------- */
export default function RecipesTab({ mode = 'all' }) {
  const { getHeaders, API, user } = useAuth();
  const navigate = useNavigate();
  const isStudio = user?.role === 'studio_chef';
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [viewingOutletsFor, setViewingOutletsFor] = useState(null); // { id, name }
  const [selected, setSelected] = useState(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState('all'); // all | food | beverage
  const [cuisineFilters, setCuisineFilters] = useState([]);  // multi-select
  const [catFilters, setCatFilters] = useState([]);          // multi-select
  const [taxonomy, setTaxonomy] = useState({ cuisines: [], categories: [] });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/recipes`, { headers: getHeaders() });
      setRecipes(r.data.items || []);
    } finally {
      setLoading(false);
    }
  }, [API, getHeaders]);

  const loadTaxonomy = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/recipes/taxonomy`, { headers: getHeaders() });
      setTaxonomy({
        cuisines: data?.cuisines || [],
        categories: data?.categories || [],
      });
    } catch (_) { /* non-fatal — UI degrades to free-text gracefully */ }
  }, [API, getHeaders]);

  useEffect(() => { load(); loadTaxonomy(); }, [load, loadTaxonomy]);

  const openNew = () => setEditing({ recipe: null });
  const openEdit = async (row) => {
    try {
      const { data } = await axios.get(`${API}/recipes/${row.id}`, { headers: getHeaders() });
      setEditing({ recipe: data });
    } catch {
      toast.error('Could not load recipe');
    }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete recipe "${row.name}"?`)) return;
    try {
      await axios.delete(`${API}/recipes/${row.id}`, { headers: getHeaders() });
      toast.success('Recipe deleted');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed');
    }
  };

  return (
    <div className="space-y-4" data-testid={mode === 'sub' ? 'sub-recipes-list-tab' : 'recipes-list-tab'}>
      <Card className="border-[#E2E8DF]">
        <CardContent className="py-3 px-4 flex items-center gap-3 flex-wrap">
          <span className="text-sm font-semibold text-[#1A2421]">
            {mode === 'sub' ? 'Sub-recipes' : 'Recipes'}
          </span>
          {(() => {
            const visible = applyFilters(recipes, mode, categoryFilter, cuisineFilters, catFilters);
            return (
              <span className="text-[11px] text-[#5C6B64]" data-testid="recipes-count">
                {visible.length} recipe{visible.length !== 1 ? 's' : ''}
              </span>
            );
          })()}

          {/* Category filter tabs */}
          <div className="ml-2 inline-flex items-center gap-1 p-0.5 rounded-md bg-[#F3F5F2] border border-[#E2E8DF]">
            {[
              { k: 'all', label: 'All' },
              { k: 'food', label: 'Food' },
              { k: 'beverage', label: 'Beverage' },
            ].map(o => (
              <button key={o.k} type="button"
                onClick={() => setCategoryFilter(o.k)}
                data-testid={`recipe-cat-filter-${o.k}`}
                className={`h-7 px-3 rounded text-[11px] font-semibold transition-colors ${categoryFilter === o.k ? 'bg-white shadow-sm text-[color:var(--color-primary)]' : 'text-[#5C6B64] hover:text-[#1A2421]'}`}>
                {o.label}
              </button>
            ))}
          </div>

          <Button size="sm" onClick={openNew}
            className="ml-auto h-9 bg-[color:var(--color-primary)] hover:bg-[#1A4D3C] text-white"
            data-testid="recipe-new-btn">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New recipe
          </Button>
        </CardContent>
      </Card>

      {/* Cuisine + Category filter chip rows — multi-select, AND filtering */}
      {(taxonomy.cuisines.length > 0 || taxonomy.categories.length > 0) && (
        <div className="space-y-2" data-testid="recipes-taxonomy-filters">
          {taxonomy.cuisines.length > 0 && (
            <FilterChipRow
              label="Cuisine"
              options={taxonomy.cuisines}
              selected={cuisineFilters}
              onToggle={(val) => setCuisineFilters(prev =>
                prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
              )}
              onClear={() => setCuisineFilters([])}
              testIdPrefix="cuisine-filter"
            />
          )}
          {taxonomy.categories.length > 0 && (
            <FilterChipRow
              label="Category"
              options={taxonomy.categories}
              selected={catFilters}
              onToggle={(val) => setCatFilters(prev =>
                prev.includes(val) ? prev.filter(v => v !== val) : [...prev, val]
              )}
              onClear={() => setCatFilters([])}
              testIdPrefix="category-filter"
            />
          )}
        </div>
      )}

      {/* Bulk actions bar — appears when selection is non-empty */}
      {selected.size > 0 && (
        <div className="sticky top-2 z-20 bg-[color:var(--color-primary)] text-white rounded-lg shadow-lg shadow-[var(--color-primary)]/30 px-4 py-2.5 flex items-center gap-3"
          data-testid="bulk-actions-bar">
          <span className="text-sm font-semibold" data-testid="bulk-selected-count">
            {selected.size} selected
          </span>
          <button type="button" onClick={() => setSelected(new Set())}
            data-testid="bulk-clear"
            className="text-[11px] underline text-white/80 hover:text-white">Clear</button>
          <div className="ml-auto flex items-center gap-2">
            <Button variant="outline" size="sm" disabled={bulkBusy}
              onClick={async () => {
                setBulkBusy(true);
                await downloadRecipeExport({ API, getHeaders, ids: [...selected], format: 'xlsx' });
                setBulkBusy(false);
              }}
              className="h-8 bg-white/10 hover:bg-white/20 border-white/30 text-white"
              data-testid="bulk-export-xlsx">
              <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Excel
            </Button>
            <Button variant="outline" size="sm" disabled={bulkBusy}
              onClick={async () => {
                setBulkBusy(true);
                await downloadRecipeExport({ API, getHeaders, ids: [...selected], format: 'pdf' });
                setBulkBusy(false);
              }}
              className="h-8 bg-white/10 hover:bg-white/20 border-white/30 text-white"
              data-testid="bulk-export-pdf">
              <FileText className="w-3.5 h-3.5 mr-1.5" /> PDF
            </Button>
          </div>
        </div>
      )}

      <Card className="border-[#E2E8DF] shadow-sm">
        <CardContent className="px-0 pb-0 pt-0">
          {(() => {
            const visible = applyFilters(recipes, mode, categoryFilter, cuisineFilters, catFilters);
            if (loading) {
              return (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin w-6 h-6 border-2 border-[color:var(--color-primary)] border-t-transparent rounded-full" />
                </div>
              );
            }
            if (visible.length === 0) {
              return (
                <div className="py-12 text-center">
                  <ChefHat className="w-8 h-8 text-[#5C6B64]/30 mx-auto mb-2" />
                  <p className="text-sm text-[#5C6B64]">
                    {mode === 'sub' ? 'No sub-recipes yet' : 'No recipes yet'}
                  </p>
                  <p className="text-xs text-[#5C6B64] mt-1">
                    {mode === 'sub'
                      ? 'Turn ON the "Use as Ingredient" switch on a weight-output recipe to see it here.'
                      : 'Click "New recipe" to build one using your ingredient master list.'}
                  </p>
                </div>
              );
            }
            return (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-wider text-[#5C6B64] font-semibold bg-[#F9F9F8] border-y border-[#E2E8DF] sticky top-0 z-[1]">
                      <th className="px-3 py-2 text-left w-8">
                        <Checkbox
                          checked={visible.length > 0 && visible.every(r => selected.has(r.id))}
                          onCheckedChange={(c) => {
                            setSelected(s => {
                              const n = new Set(s);
                              if (c) visible.forEach(r => n.add(r.id));
                              else visible.forEach(r => n.delete(r.id));
                              return n;
                            });
                          }}
                          data-testid="bulk-select-all" />
                      </th>
                      <th className="px-4 py-2 text-left">Recipe</th>
                      <th className="px-3 py-2 text-center">Category</th>
                      <th className="px-4 py-2 text-center">Type</th>
                      <th className="px-4 py-2 text-center">Lines</th>
                      <th className="px-4 py-2 text-right">Total cost</th>
                      <th className="px-4 py-2 text-right">Cost / unit</th>
                      <th className="px-4 py-2 text-center">Assigned</th>
                      <th className="px-4 py-2 text-center">Updated</th>
                      <th className="px-4 py-2 text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map(r => (
                      <tr key={r.id} className="border-b border-[#E2E8DF]/60 hover:bg-[#F9F9F8]"
                        data-testid={`recipe-row-${r.id}`}>
                        <td className="px-3 py-2">
                          <Checkbox
                            checked={selected.has(r.id)}
                            onCheckedChange={(c) => {
                              setSelected(s => {
                                const n = new Set(s);
                                if (c) n.add(r.id); else n.delete(r.id);
                                return n;
                              });
                            }}
                            data-testid={`bulk-select-${r.id}`} />
                        </td>
                        <td className="px-4 py-2">
                          <div className="flex flex-col gap-1">
                            <div className="flex items-center gap-1.5">
                              {r.is_sub_recipe && (
                                <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-[#1D4ED8] bg-[#1D4ED8]/10 border border-[#1D4ED8]/20 px-1.5 py-0.5 rounded uppercase tracking-wider">
                                  <Layers className="w-2.5 h-2.5" /> Sub
                                </span>
                              )}
                              <span className="font-medium text-[#1A2421]">{r.name}</span>
                            </div>
                            {/* Cuisine + Category tag chips */}
                            {(r.cuisine || r.category) ? (
                              <div className="flex items-center gap-1 flex-wrap">
                                {r.cuisine && (
                                  <span className="inline-flex items-center text-[9px] font-semibold text-[#7A2A12] bg-[#FCE7D6] px-1.5 py-0.5 rounded"
                                    data-testid={`recipe-row-cuisine-${r.id}`}>
                                    {r.cuisine}
                                  </span>
                                )}
                                {r.category && (
                                  <span className="inline-flex items-center text-[9px] font-semibold text-[#3A1F12] bg-[#FAF3EA] border border-[#E8DECF] px-1.5 py-0.5 rounded"
                                    data-testid={`recipe-row-category-${r.id}`}>
                                    {r.category}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="inline-flex items-center text-[9px] font-semibold text-[#9CA9A1] bg-stone-50 border border-stone-200 px-1.5 py-0.5 rounded w-fit"
                                data-testid={`recipe-row-uncategorized-${r.id}`}>
                                Uncategorized
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-2 text-center">
                          {(r.category_type || 'food') === 'beverage' ? (
                            <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-[#1D4ED8] bg-[#1D4ED8]/10 border border-[#1D4ED8]/20 px-1.5 py-0.5 rounded uppercase tracking-wider" data-testid={`recipe-cat-${r.id}`}>
                              Beverage
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-[9px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded uppercase tracking-wider" data-testid={`recipe-cat-${r.id}`}>
                              Food
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center text-[11px] text-[#5C6B64] uppercase tracking-wider">
                          {r.output_type === 'weight'
                            ? `${((r.output_kg || 0) + (r.output_grams || 0) / 1000).toFixed(3)} kg`
                            : `${r.output_portions || 0} portion${(r.output_portions || 0) === 1 ? '' : 's'}`}
                        </td>
                        <td className="px-4 py-2 text-center text-xs text-[#5C6B64]">{r.ingredient_count}</td>
                        <td className="px-4 py-2 text-right font-semibold text-[#1A2421] tabular-nums">{fmtINR(r.total_cost)}</td>
                        <td className="px-4 py-2 text-right font-bold text-[color:var(--color-primary)] tabular-nums">
                          {r.output_type === 'weight' ? `${fmtINR(r.cost_per_kg)}/kg` : `${fmtINR(r.cost_per_portion)}/portion`}
                        </td>
                        <td className="px-4 py-2 text-center">
                          {(r.outlet_ids || []).length > 0 ? (
                            <button type="button"
                              onClick={() => setViewingOutletsFor({ id: r.id, name: r.name })}
                              data-testid={`recipe-view-outlets-${r.id}`}
                              className="inline-flex items-center gap-1 text-[10px] font-semibold text-[color:var(--color-primary)] hover:underline">
                              <Eye className="w-3 h-3" /> {(r.outlet_ids || []).length} outlet{(r.outlet_ids || []).length === 1 ? '' : 's'}
                            </button>
                          ) : (
                            <span className="text-[10px] text-[#5C6B64] italic">Not assigned</span>
                          )}
                        </td>
                        <td className="px-4 py-2 text-center text-[10px] text-[#5C6B64] tabular-nums">
                          {r.updated_at ? fmtDateIST(r.updated_at) : '—'}
                        </td>
                        <td className="px-4 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <button type="button"
                                  data-testid={`recipe-row-export-${r.id}`}
                                  className="w-7 h-7 rounded-md text-[#5C6B64] hover:bg-[#F3F5F2] flex items-center justify-center">
                                  <Download className="w-3.5 h-3.5" />
                                </button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => downloadRecipeExport({ API, getHeaders, id: r.id, format: 'xlsx' })}>
                                  <FileSpreadsheet className="w-3.5 h-3.5 mr-1.5" /> Excel (.xlsx)
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => downloadRecipeExport({ API, getHeaders, id: r.id, format: 'pdf' })}>
                                  <FileText className="w-3.5 h-3.5 mr-1.5" /> PDF
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => downloadRecipeExport({ API, getHeaders, id: r.id, format: 'jpg', variant: 'social' })}
                                  data-testid={`recipe-row-export-jpg-social-${r.id}`}>
                                  <Instagram className="w-3.5 h-3.5 mr-1.5" /> Instagram card
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => downloadRecipeExport({ API, getHeaders, id: r.id, format: 'jpg', variant: 'full' })}
                                  data-testid={`recipe-row-export-jpg-full-${r.id}`}>
                                  <ImageIcon className="w-3.5 h-3.5 mr-1.5" /> Full recipe JPG
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                            <button onClick={() => openEdit(r)}
                              data-testid={`recipe-edit-${r.id}`}
                              className="w-7 h-7 rounded-md text-[color:var(--color-primary)] hover:bg-[color:var(--color-primary)]/10 flex items-center justify-center">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            {isStudio && !r.is_sub_recipe && (
                              <button
                                onClick={() => navigate(`/studio/production?recipe=${r.id}&qty=4`)}
                                data-testid={`recipe-plan-${r.id}`}
                                title="Plan this recipe (Production Planner, qty 4)"
                                className="w-7 h-7 rounded-md text-[#7A2A12] hover:bg-[#FCE7D6]/70 flex items-center justify-center">
                                <Calendar className="w-3.5 h-3.5" />
                              </button>
                            )}
                            <button onClick={() => handleDelete(r)}
                              data-testid={`recipe-delete-${r.id}`}
                              className="w-7 h-7 rounded-md text-[#C45A41] hover:bg-[#C45A41]/10 flex items-center justify-center">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            );
          })()}
        </CardContent>
      </Card>

      {editing && (
        <RecipeBuilder
          recipe={editing.recipe}
          onClose={() => { setEditing(null); load(); loadTaxonomy(); }}
          onSaved={load}
          taxonomy={taxonomy}
          refreshTaxonomy={loadTaxonomy}
        />
      )}

      {viewingOutletsFor && (
        <ViewOutletsDialog
          open={!!viewingOutletsFor}
          onClose={() => setViewingOutletsFor(null)}
          recipeId={viewingOutletsFor.id}
          recipeName={viewingOutletsFor.name}
        />
      )}
    </div>
  );
}
